import React from 'react';
import { Image, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

interface ImageBlockEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const ImageBlockEditor: React.FC<ImageBlockEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onContentChange({ [name]: value });
  };
  
  const handleAlignmentChange = (alignment: 'left' | 'center' | 'right') => {
    onSettingsChange({ imageAlign: alignment });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Görsel Bloğu'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="showCaption"
                checked={settings?.showCaption !== false}
                onChange={handleSettingChange}
                className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
              />
              <span className="text-sm text-slate-600">Açıklamayı Göster</span>
            </label>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Görsel Hizalama
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleAlignmentChange('left')}
              className={`p-2 rounded-lg ${
                settings?.imageAlign === 'left'
                  ? 'bg-[#F7BA45]/10 text-[#F7BA45] border border-[#F7BA45]/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
              }`}
            >
              <AlignLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => handleAlignmentChange('center')}
              className={`p-2 rounded-lg ${
                settings?.imageAlign === 'center' || !settings?.imageAlign
                  ? 'bg-[#F7BA45]/10 text-[#F7BA45] border border-[#F7BA45]/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
              }`}
            >
              <AlignCenter size={20} />
            </button>
            <button
              type="button"
              onClick={() => handleAlignmentChange('right')}
              className={`p-2 rounded-lg ${
                settings?.imageAlign === 'right'
                  ? 'bg-[#F7BA45]/10 text-[#F7BA45] border border-[#F7BA45]/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
              }`}
            >
              <AlignRight size={20} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">İçerik</h3>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Görsel URL
          </label>
          <div className="relative">
            <Image className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              name="imageUrl"
              value={content?.imageUrl || ''}
              onChange={handleContentChange}
              placeholder="https://example.com/image.jpg"
              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          <p className="text-xs text-slate-500">
            Görsel URL'si girin. Örneğin: https://images.unsplash.com/photo-xxx
          </p>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Görsel Açıklaması
          </label>
          <input
            type="text"
            name="caption"
            value={content?.caption || ''}
            onChange={handleContentChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            placeholder="Görsel açıklaması"
          />
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Alt Metin (SEO için)
          </label>
          <input
            type="text"
            name="altText"
            value={content?.altText || ''}
            onChange={handleContentChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            placeholder="Görsel alt metni"
          />
        </div>
      </div>
    </div>
  );
};

export default ImageBlockEditor;